// apps/roleforge-app/src/pages/AuditDashboardPage.tsx
//
// RoleForge v2 Plan 2, Task 2 — the delivery page. This is the customer's
// durable home after purchase (owner decision: deliverables live in RoleForge;
// the VibeControl Welcome widget links back here). Replaces the old
// "Your Blueprints" stub.
//
// Per audit: blueprint status (continue vs complete), Blueprint Bundle
// re-download (always available once complete), and the fast path into
// VibeControl. Below the audits: the DIY Implementation Course card, driven
// by `GET /api/roleforge/course-entitlement` (server endpoint lands in Task 3; a failed
// fetch renders a neutral "unknown" state, never a crash and never a hard
// "you don't have this"). The not-entitled re-offer card is a placeholder
// that Task 4 wires up.
//
// Tone rule for this file: calm. No urgency, no countdowns, no pressure copy.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import {
  ApiError,
  getCourseEntitlement,
  postTrialConsentAccount,
  listAudits,
  type AuditRow,
  type AuditStatus,
} from '../lib/api-client';
import { OTO_LEDGER_ROWS } from '../lib/oto-ledger';
import { useBundleDownload } from '../lib/bundle-download';
import { getTargetWorkspaceId } from '../lib/workspace';

// ─── Status semantics ────────────────────────────────────────────────────────
//
// The Blueprint itself is complete once discovery has finished — that is
// `ready_to_materialize` and everything after it. Materialization is the
// optional fast path, not part of Blueprint completeness, so the bundle
// download is available from `ready_to_materialize` onward (matches the
// Activation Moment, which appears at that same status).
//
// `failed` is written by MATERIALIZATION failure (roleforge-materialize.ts)
// — i.e. after discovery finished — so the paid bundle stays downloadable
// there; only the fast-path copy changes. `archived` renders inert.

const COMPLETE_STATUSES: readonly AuditStatus[] = [
  'ready_to_materialize',
  'materializing',
  'materialized',
  'failed',
];

export function isBlueprintComplete(status: AuditStatus): boolean {
  return COMPLETE_STATUSES.includes(status);
}

function statusLabel(status: AuditStatus): string {
  switch (status) {
    case 'materialized':
      return 'Blueprint complete — AI team live in VibeControl';
    case 'materializing':
      return 'Blueprint complete — AI team being set up';
    case 'ready_to_materialize':
      return 'Blueprint complete';
    case 'failed':
      return 'Blueprint complete — automatic setup needs attention';
    case 'archived':
      return 'Archived';
    default:
      return 'Blueprint in progress';
  }
}

// ─── Bundle download card ────────────────────────────────────────────────────

function BundleDownload({ auditId }: { auditId: string }) {
  const { busy, error, download } = useBundleDownload(auditId);

  return (
    <div className="space-y-1">
      <h3 className="text-sm font-semibold text-ink">Your Blueprint Bundle</h3>
      <p className="text-sm text-graphite">
        Your report, seat charters, and portable skill folders — yours to keep.
        You can re-download it any time.
      </p>
      <button
        type="button"
        onClick={download}
        disabled={busy}
        data-testid="bundle-download"
        className="mt-1 inline-flex items-center rounded-lg bg-plum px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
      >
        {busy ? 'Preparing your bundle…' : 'Download your Blueprint Bundle'}
      </button>
      {error && (
        <p role="status" aria-live="polite" data-testid="bundle-download-error" className="text-xs text-ember">
          {error.message}
        </p>
      )}
    </div>
  );
}

// ─── Per-audit card ──────────────────────────────────────────────────────────

function AuditCard({ audit }: { audit: AuditRow }) {
  const archived = audit.status === 'archived';
  const failed = audit.status === 'failed';
  const complete = !archived && isBlueprintComplete(audit.status);
  return (
    <section
      data-testid={`audit-card-${audit.id}`}
      className="space-y-5 rounded-2xl border border-line bg-card p-6 shadow-soft"
    >
      <header className="space-y-1">
        <h2 className="text-lg font-semibold text-ink">AI Workforce Blueprint</h2>
        <p className="text-sm text-graphite">
          <span data-testid="audit-status">{statusLabel(audit.status)}</span>
          {' · '}started {new Date(audit.created_at).toLocaleDateString()}
        </p>
      </header>

      {archived ? (
        <p className="text-sm text-graphite" data-testid="archived-note">
          This Blueprint has been archived.
        </p>
      ) : complete ? (
        <>
          <BundleDownload auditId={audit.id} />
          <div className="space-y-1 border-t border-line pt-4">
            <h3 className="text-sm font-semibold text-ink">Fast path</h3>
            {failed ? (
              <p className="text-sm text-graphite" data-testid="failed-note">
                The automatic setup hit a problem — your Blueprint and your
                bundle are safe. You can try the fast path again whenever
                you're ready.
              </p>
            ) : (
              <p className="text-sm text-graphite">
                Prefer not to set things up by hand? VibeControl can set up
                your workforce from this Blueprint for you.
              </p>
            )}
            <Link
              to={`/audits/${audit.id}/materialize`}
              data-testid="fast-path-link"
              className="mt-1 inline-flex items-center rounded-lg border border-line px-4 py-2 text-sm font-semibold text-ink hover:bg-card-hover"
            >
              {failed
                ? 'Try the automatic setup again →'
                : 'Set it up in VibeControl →'}
            </Link>
          </div>
        </>
      ) : (
        <div className="space-y-1">
          <p className="text-sm text-graphite">
            Pick up where you left off — your progress is saved.
          </p>
          <Link
            to={`/audits/${audit.id}`}
            data-testid="continue-link"
            className="mt-1 inline-flex items-center rounded-lg bg-plum px-4 py-2 text-sm font-semibold text-white"
          >
            Continue your Blueprint →
          </Link>
        </div>
      )}
    </section>
  );
}

// ─── Course card ─────────────────────────────────────────────────────────────
//
// Three states: entitled ("Open the course"), not entitled (the Task 4 late
// re-offer — the "not really an OTO"), unknown (fetch failed — neutral, no
// claim either way).

export type CourseEntitlementState = 'entitled' | 'not_entitled' | 'unknown';

// The delivery-page re-offer. The OTO ledger's three lines, condensed; one
// calm CTA with a confirm step; on success the card flips to entitled (via
// onAccepted). 409 = the owner revoked a prior consent — no self-serve
// re-consent, just calm support copy.
function ReofferBody({ jwt, onAccepted }: { jwt: string | null; onAccepted: () => void }) {
  const [phase, setPhase] = useState<'idle' | 'confirming' | 'busy'>('idle');
  const [error, setError] = useState<{ revoked: boolean; message: string } | null>(null);

  const accept = async () => {
    if (!jwt) {
      // A missing session mid-click reads the same as any transient failure:
      // calm copy, nothing changed, try again (a silent no-op would leave a
      // busy-looking button that never resolves).
      setError({
        revoked: false,
        message:
          "That didn't go through just now. Nothing has changed on your account — you can try again whenever you like.",
      });
      setPhase('idle');
      return;
    }
    setPhase('busy');
    setError(null);
    try {
      await postTrialConsentAccount(jwt);
      onAccepted();
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        setError({
          revoked: true,
          message:
            "This offer isn't available on your account — if that seems wrong, contact support and we'll sort it out.",
        });
      } else {
        setError({
          revoked: false,
          message:
            "That didn't go through just now. Nothing has changed on your account — you can try again whenever you like.",
        });
      }
      setPhase('idle');
    }
  };

  if (error?.revoked) {
    return (
      <p className="text-sm text-graphite" data-testid="course-reoffer-revoked">
        {error.message}
      </p>
    );
  }

  return (
    <>
      <p className="text-sm text-graphite">
        The implementation course isn't part of your order yet. The free
        upgrade from your purchase is still here — no rush, and nothing is
        charged today.
      </p>
      <ul className="space-y-1" data-testid="course-reoffer-ledger">
        {OTO_LEDGER_ROWS.map((row) => (
          <li key={row.label} className="flex items-baseline justify-between gap-4 text-sm">
            <span className="text-ink">{row.label}</span>
            <span className="shrink-0 font-semibold text-graphite">{row.amount}</span>
          </li>
        ))}
      </ul>
      {phase === 'confirming' || phase === 'busy' ? (
        <div className="space-y-2">
          <p className="text-sm text-graphite" data-testid="course-reoffer-confirm-note">
            This unlocks the course and arms your 14-day trial for whenever you
            choose to activate — nothing is charged today.
          </p>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={accept}
              disabled={phase === 'busy'}
              data-testid="course-reoffer-confirm"
              className="inline-flex items-center rounded-lg bg-plum px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
            >
              {phase === 'busy' ? 'Adding your upgrade…' : 'Confirm free upgrade'}
            </button>
            <button
              type="button"
              onClick={() => setPhase('idle')}
              disabled={phase === 'busy'}
              data-testid="course-reoffer-cancel"
              className="text-sm font-semibold text-graphite hover:text-ink disabled:opacity-50"
            >
              Not now
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => {
            // Re-entering the confirm step starts fresh — a stale "didn't go
            // through" note under a new confirm prompt reads as a live failure.
            setError(null);
            setPhase('confirming');
          }}
          data-testid="course-reoffer-cta"
          className="mt-1 inline-flex items-center rounded-lg border border-line px-4 py-2 text-sm font-semibold text-ink hover:bg-card-hover"
        >
          Get the free upgrade
        </button>
      )}
      {error && (
        <p role="status" aria-live="polite" data-testid="course-reoffer-error" className="text-xs text-ember">
          {error.message}
        </p>
      )}
    </>
  );
}

function CourseCard({
  state,
  jwt,
  onAccepted,
}: {
  state: CourseEntitlementState;
  jwt: string | null;
  onAccepted: () => void;
}) {
  return (
    <section
      data-testid="course-card"
      className="space-y-2 rounded-2xl border border-line bg-card p-6 shadow-soft"
    >
      <h2 className="text-lg font-semibold text-ink">DIY Implementation Course</h2>
      {state === 'entitled' && (
        <>
          <p className="text-sm text-graphite">
            Step-by-step lessons for putting your Blueprint to work yourself.
          </p>
          <Link
            to="/course"
            data-testid="course-open-link"
            className="mt-1 inline-flex items-center rounded-lg bg-plum px-4 py-2 text-sm font-semibold text-white"
          >
            Open the course →
          </Link>
        </>
      )}
      {state === 'not_entitled' && <ReofferBody jwt={jwt} onAccepted={onAccepted} />}
      {state === 'unknown' && (
        <p className="text-sm text-graphite" data-testid="course-unknown">
          We couldn't check your course access just now. Refresh in a moment —
          nothing about your order has changed.
        </p>
      )}
    </section>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

export function AuditDashboardPage() {
  const { jwt } = useAuth();
  const [audits, setAudits] = useState<AuditRow[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [courseState, setCourseState] = useState<CourseEntitlementState | null>(null);

  useEffect(() => {
    if (!jwt) return;
    let cancelled = false;

    void (async () => {
      try {
        const ws = await getTargetWorkspaceId();
        if (cancelled) return;
        if (!ws) {
          setAudits([]);
          return;
        }
        const { audits: rows } = await listAudits(jwt, ws);
        if (!cancelled) setAudits(rows);
      } catch (e) {
        if (!cancelled) setLoadError(e instanceof Error ? e.message : String(e));
      }
    })();

    // Independent of the audit list — a course-endpoint failure must not
    // block the page (Task 3 hasn't shipped the endpoint yet).
    void (async () => {
      try {
        const { entitled } = await getCourseEntitlement(jwt);
        if (!cancelled) setCourseState(entitled ? 'entitled' : 'not_entitled');
      } catch {
        if (!cancelled) setCourseState('unknown');
      }
    })();

    return () => { cancelled = true; };
  }, [jwt]);

  if (loadError) {
    return (
      <div className="space-y-2">
        <h1 className="text-2xl font-semibold text-ink">Your Blueprint</h1>
        <p className="text-sm text-ember" data-testid="dashboard-error">
          We couldn't load your Blueprints: {loadError}. Refresh to try again —
          nothing is lost.
        </p>
      </div>
    );
  }

  if (audits === null) {
    return <div className="p-8 text-graphite">Loading your Blueprint…</div>;
  }

  return (
    <div className="space-y-8">
      <header className="space-y-2">
        <h1 className="font-display text-3xl font-semibold tracking-tight text-ink">
          Your Blueprint
        </h1>
        <p className="max-w-2xl text-graphite">
          Everything from your AI Workforce Blueprint lives here — come back
          any time.
        </p>
      </header>

      {audits.length === 0 ? (
        <section className="space-y-3 rounded-2xl border border-line bg-card p-6 shadow-soft">
          <p className="text-graphite">
            Start your Blueprint to see it here.
          </p>
          <Link
            to="/start"
            className="inline-block rounded-lg bg-plum px-5 py-2 font-semibold text-white"
          >
            Start your Blueprint →
          </Link>
        </section>
      ) : (
        <div className="space-y-6">
          {audits.map((audit) => (
            <AuditCard key={audit.id} audit={audit} />
          ))}
        </div>
      )}

      {courseState !== null && (
        <CourseCard
          state={courseState}
          jwt={jwt}
          onAccepted={() => setCourseState('entitled')}
        />
      )}
    </div>
  );
}
